import { useEffect, useState } from "react"
import { Sparkles, AlertCircle, RefreshCw } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/cn"
import type { ActionPayload, RefreshEnvelope, V1Document } from "./types"
import type { CustomerContextAppSummary } from "@/types/ticket"
import { DocumentRenderer } from "./document-renderer"
import { useAction } from "./use-action"

type Props = {
  ticketId: string
  apps: CustomerContextAppSummary[]
}

// Customer Context tab content. One section per installed app, stacked
// in `position` order. Each app loads independently — one app failing
// (404 / role-blocked / fetch_5xx / invalid_document) doesn't break the
// others. Lazy-loads on first mount; the tab caller controls when that
// happens (mounting only once the user clicks the tab).
export function CustomerContextPane({ ticketId, apps }: Props) {
  if (!apps || apps.length === 0) {
    return (
      <Card variant="inset" className="p-6 text-center text-sm text-app-subtle">
        <Sparkles size={20} strokeWidth={1.5} className="mx-auto mb-2 opacity-60" />
        <div className="font-medium text-app-text">No customer context apps installed.</div>
        <p className="mt-1 text-app-muted">
          Contact your admin to enable a Valet App for this surface.
        </p>
      </Card>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {apps.map((app) => (
        <CustomerContextSection key={app.companyAppId} ticketId={ticketId} app={app} />
      ))}
    </div>
  )
}

type FetchState =
  | { kind: "loading" }
  | { kind: "ok"; document: V1Document }
  | {
      kind:           "error"
      errorKind:      string | null
      message:        string
      // Internal-only debug surface — the operator (often the dev
      // building the partner-side endpoint) needs to see the actual
      // partner response without grepping AppHealthEvent.
      statusCode?:    number | null
      remoteReason?:  string | null
      remoteMessage?: string | null
      remoteBody?:    string | null
    }

function CustomerContextSection({
  ticketId, app,
}: {
  ticketId: string
  app: CustomerContextAppSummary
}) {
  const [state, setState] = useState<FetchState>({ kind: "loading" })
  const { runAction } = useAction(ticketId, app.companyAppId)

  async function load() {
    setState({ kind: "loading" })
    try {
      const res = await fetch(
        `/tickets/${ticketId}/company_apps/${app.companyAppId}/document`,
        { credentials: "same-origin", headers: { "Accept": "application/json" } },
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json?.ok === false) {
        setState({
          kind:          "error",
          errorKind:     json?.error_kind ?? null,
          message:       json?.error || `Failed to load (${res.status}).`,
          statusCode:    json?.status_code ?? null,
          remoteReason:  json?.remote_reason ?? null,
          remoteMessage: json?.remote_message ?? null,
          remoteBody:    json?.remote_body ?? null,
        })
        return
      }
      setState({ kind: "ok", document: json.document as V1Document })
    } catch (e) {
      setState({
        kind: "error",
        errorKind: "network_error",
        message:   e instanceof Error ? e.message : "Failed to load.",
      })
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, app.companyAppId])

  // The renderer's `onAction` returns the partial-refresh envelope so
  // matching block ids swap in place; for `scope: full` we re-fetch the
  // whole document so subsequent renders see the canonical state.
  async function onAction(action: ActionPayload, refresh?: string[]): Promise<RefreshEnvelope | null> {
    return runAction(action, refresh)
  }

  return (
    <Card variant="inset" className="space-y-3 p-4">
      <SectionHeader
        app={app}
        loading={state.kind === "loading"}
        onReload={load}
      />
      {state.kind === "loading" && (
        <div className="py-4 text-center text-xs text-app-subtle">Loading…</div>
      )}
      {state.kind === "error" && (
        <AppErrorCard
          appKey={app.appKey}
          errorKind={state.errorKind}
          message={state.message}
          statusCode={state.statusCode ?? null}
          remoteReason={state.remoteReason ?? null}
          remoteMessage={state.remoteMessage ?? null}
          remoteBody={state.remoteBody ?? null}
          onRetry={load}
        />
      )}
      {state.kind === "ok" && (
        <DocumentRenderer
          document={state.document}
          onAction={onAction}
          onFullRefresh={load}
        />
      )}
    </Card>
  )
}

function SectionHeader({
  app, loading, onReload,
}: {
  app: CustomerContextAppSummary
  loading: boolean
  onReload: () => void
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <Sparkles size={14} strokeWidth={2} className="shrink-0 text-brand-red" />
        <h3 className="truncate text-sm font-semibold text-app-text">{app.appName}</h3>
        {app.installState === "testing" && (
          <span className="rounded-chip border border-status-warning/40 bg-status-warning/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-status-warning">
            Testing
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onReload}
        disabled={loading}
        title="Reload"
        className={cn(
          "inline-flex h-7 w-7 items-center justify-center rounded-control text-app-muted transition",
          "hover:bg-white/5 hover:text-app-text disabled:opacity-50",
        )}
      >
        <RefreshCw size={12} strokeWidth={2} className={cn(loading && "animate-spin")} />
      </button>
    </div>
  )
}

// Inline error card for one app. Other apps in the same tab keep
// rendering — the partner debug panel (per app) shows the structural
// `error_kind` for ops to triage.
function AppErrorCard({
  appKey, errorKind, message, statusCode, remoteReason, remoteMessage, remoteBody, onRetry,
}: {
  appKey: string
  errorKind: string | null
  message: string
  statusCode: number | null
  remoteReason: string | null
  remoteMessage: string | null
  remoteBody: string | null
  onRetry: () => void
}) {
  const heading = errorHeading(errorKind, statusCode)
  // Show the partner's actual response in a collapsed-by-default details
  // pane. Internal-tool — we expect the operator to be the dev building
  // the partner endpoint, so the more raw signal the better.
  const hasRemoteDetail = (statusCode != null && statusCode > 0) || remoteReason || remoteMessage || remoteBody

  return (
    <div className="rounded-control border border-status-error/30 bg-status-error/10 p-3 text-sm">
      <div className="flex items-start gap-2">
        <AlertCircle size={14} strokeWidth={2} className="mt-0.5 shrink-0 text-status-error" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-status-error">
            {heading ?? `Couldn't load ${appKey}`}
          </div>
          <p className="mt-0.5 text-xs text-app-muted">{message}</p>
        </div>
      </div>
      {hasRemoteDetail && (
        <details className="mt-2 text-xs text-app-muted">
          <summary className="cursor-pointer select-none font-medium hover:text-app-text">
            Partner response
          </summary>
          <dl className="mt-1.5 space-y-0.5 pl-3 font-mono">
            {statusCode != null && statusCode > 0 && (
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-app-subtle">status</dt>
                <dd className="break-all">{statusCode}</dd>
              </div>
            )}
            {remoteReason && (
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-app-subtle">reason</dt>
                <dd className="break-all">{remoteReason}</dd>
              </div>
            )}
            {remoteMessage && (
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-app-subtle">message</dt>
                <dd className="break-all whitespace-pre-wrap">{remoteMessage}</dd>
              </div>
            )}
            {remoteBody && (
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-app-subtle">body</dt>
                <dd className="break-all whitespace-pre-wrap">{remoteBody}</dd>
              </div>
            )}
          </dl>
        </details>
      )}
      <div className="mt-2 flex justify-end">
        <Button variant="ghost" onClick={onRetry} className="px-3 py-1 text-xs">
          Retry
        </Button>
      </div>
    </div>
  )
}

function errorHeading(kind: string | null, statusCode: number | null): string | null {
  switch (kind) {
    case "role_blocked":     return "You don't have access to this app."
    case "install_disabled": return "This app is disabled."
    case "install_testing":  return "This app is in testing mode."
    case "fetch_failed":
    case "fetch_4xx": {
      if (statusCode != null && statusCode > 0) {
        return `Partner returned ${statusCode} ${httpStatusText(statusCode)}`.trim()
      }
      return "The partner endpoint rejected the request."
    }
    case "fetch_5xx": {
      if (statusCode != null && statusCode >= 500) {
        return `Partner is failing (${statusCode} ${httpStatusText(statusCode)})`.trim()
      }
      return "The partner endpoint is failing."
    }
    case "fetch_timeout":      return "The partner endpoint timed out."
    case "invalid_document":   return "The document failed validation."
    case "context_bag_failed": return "Could not assemble the request context."
    case "no_endpoint":        return "This app has no document endpoint configured."
    case "internal_error":     return "Something went wrong inside Valet."
    default:                   return null
  }
}

function httpStatusText(s: number): string {
  switch (s) {
    case 400: return "Bad Request"
    case 401: return "Unauthorized"
    case 403: return "Forbidden"
    case 404: return "Not Found"
    case 409: return "Conflict"
    case 422: return "Unprocessable Entity"
    case 429: return "Too Many Requests"
    case 500: return "Internal Server Error"
    case 502: return "Bad Gateway"
    case 503: return "Service Unavailable"
    case 504: return "Gateway Timeout"
    default:  return ""
  }
}
